import { parseAll } from "./parse.js";
import {
  translateAggregate,
  translateAggregateLevels,
  translateDisaggregate
} from "./translate/aggregate.js";
import {
  translateRemoveChannel,
  translateRescaleSizeChannel
} from "./translate/encoding.js";
import {
  translateChangeNFacetColumns,
  translateMakeSplit
} from "./translate/facet.js";
import { translateAddDataFilter } from "./translate/filter.js";
import { translateDot2 } from "./translate/fromDot.js";
import {
  translateMap2BarsNposXQpos,
  translateMap2Multiples
} from "./translate/fromMap.js";
import {
  translateMakeHeatmapNoAgg,
  translateMakeHeatmapNoAggDup,
  translateMakeHeatmapNonTabularNoAggConvertChannel,
  translateMAkeHeatmapNonTabularRemoveNominalChannels,
  translateRepositionMarkLabelForHeatmap,
  translateMakeheatmapNominalColorToFill
} from "./translate/makeHeatmap.js";
import {
  translateRemoveMarkLabel,
  translateReplaceMarkLabel2AxisLabel,
  translateAddMarkLabel,
  translateExternalizeMarkLabel,
  translateNumberMarkLabel
} from "./translate/markLabel.js";
import { translatePartialTranspose } from "./translate/partialTranspose.js";
import {
  translateProportionateResizing,
  translateDisproportionateResizing,
  translateIgnoreHeight
} from "./translate/sizing.js";
import {
  translateConvertAxisLabelToLegend,
  translateConvertLegendToAxisLabel,
  translateConvertMarkLabelToAxisLabel,
  translateConvertMarkLabelToLegend,
  translateRepositionLegend,
  translateAddLegend,
  translateRemoveAxisGrid
} from "./translate/reference.js";
import {
  translateRemoveText,
  translateUnwrapText,
  translateWrapText,
  translateWrapTextBy,
  translateReduceTextSize,
  translateUnwrapBrText,
  translateSimplifyTextMinMaxSplit,
  translateChangeTextFlex,
  translateConvertTextOrient
} from "./translate/text.js";
import {
  addTransposeAdjustment,
  translateTranspose
} from "./translate/transpose.js";
import { search_asp } from "./util.js";
import {
  translateRemoveAnnotation,
  translateWrapAnnotationBy,
  translateExternalizeAnnotation,
  translateExternalizeDataAnnotation,
  translateNumberAnnotation,
  translateInternalizeAnnotation,
  translateConvertAnnotation2Title
} from "./translate/annotation.js";
import { getDescription } from "./describe.js";
import {
  translateAddZoomToMap,
  translateAddContextToBar
} from "./translate/interaction.js";
import {
  translateRemoveTextPartial,
  translateAddAxisLabel,
  translateAddTextPartial,
  translateParallelizeText,
  translateRemoveTickValues
} from "./translate/axisLabel.js";
import {
  translateInternalizeTitle,
  translateRemoveTitle,
  translateReduceTitleFontSize,
  translateIgnoreTitleWidth
} from "./translate/title.js";
import {
  translateRepositionTooltip,
  translateAddTooltip,
  translateRemoveTooltip,
  translateAddTooltipField
} from "./translate/tooltip.js";
import {
  translateRemovePointOnMark,
  translateAddPointOnMark,
  translateRemoveEndMark,
  translateAddEndMark,
  translateConvertAreaToLine,
  translateConvertLineToArea,
  translateInvertCircleMark
} from "./translate/markDeco.js"
import {
  translateLeaveMinMax,
  translateLeaveHighlightOnly,
  translateLineMultiplesToOveralys,
  translateAggregateMultiples,
  translateAddMarkHighlights
} from "./translate/forTiny.js";
import {
  translateCompress2dDotPlot,
  translateBarTo1dDotPlot
} from "./translate/makeDot.js";

export async function translate(asp_lines, source, mapping, config) {
  let rec_config = config.recommender;
  let translated = {
    name: [rec_config.specname || "original", rec_config.intended_screen || "transform"].join("-"),
    description: `This is a Cicero specification for ${rec_config.title || "Original View"} from ${rec_config.original_screen_type || "Type 1"} to ${rec_config.intended_screen_type || "Type 2"}.`,
    metadata: {
      condition: rec_config.intended_screen || "",
      mediaType: "screen",
      aspectRatio: rec_config.intended_orient || ""
    },
    transformations: []
  };
  let parsed = await parseAll(asp_lines || []);
  let descriptions = [];
  let processed = [];
  let at_the_end = [];
  let at_first = [];

  // process_sizing
  let old_w = parseInt(source?.layout?.width), old_h = parseInt(source?.layout?.height);
  config.old_size = [old_w, old_h];
  let new_w = -1, new_h = -1;
  if (old_w !== undefined && old_h !== undefined) {
    let dis = search_asp(parsed, "do(disproportionate_resizing", 'startsWith');
    let pro = search_asp(parsed, "do(proportionate_resizing", 'startsWith');
    let igh = search_asp(parsed, "do(ignore_height", 'startsWith');
    if (dis.length == 1) {
      new_w = dis[0].args[1];
      new_h = dis[0].args[2];
    } else if (pro.length == 1) {
      new_w = pro[0].args[1];
      new_h = old_h / old_w * new_w;
    } else if (igh.length == 0) {
      new_w = old_w;
      new_h = old_h;
    }
  }
  config.new_size = [new_w, new_h];
  config.has_old_new_size = [config.old_size[0], config.old_size[1], config.new_size[0], config.new_size[1]].every(d => d >= 0)
  config.has_old_new_width = [config.old_size[0], config.new_size[0]].every(d => d >= 0)

  for (const line of parsed.strategies) {
    let t = lineTranslate(line, parsed, source, mapping, processed, config);
    descriptions.push(line.args)
    // pust t to transformations;
    if (t.note.includes("prepend")) at_first.push(...t.rules);
    else if (t.note.includes("transpose_related") || t.note.includes("removals")) at_the_end.push(...t.rules);
    else {
      translated.transformations.push(...t.rules);
      processed.push(line);
    }
  }
  if (at_first.length > 0) translated.transformations = at_first.concat(translated.transformations);
  translated.transformations.push(...at_the_end);
  descriptions = getDescription(descriptions)
  return { translated, parsed, descriptions };
}

function lineTranslate(line, parsed, source, mapping, processed, config) {
  let rules = [], note = [];
  if (line.pred !== "do") return [];
  let tech = line.args[0];
  let args = line.args.slice(1);
  // todo: separate file
  // [layout]
  // sizing
  if (tech === 'proportionate_resizing') {
    rules = translateProportionateResizing(tech, args, line, parsed, source, mapping, processed, config);
    note.push("prepend");
  } else if (tech === 'disproportionate_resizing') {
    rules = translateDisproportionateResizing(tech, args, line, parsed, source, mapping, processed, config);
    note.push("prepend");
  } else if (tech === 'ignore_height') {
    rules = translateIgnoreHeight(tech, args, line, parsed, source, mapping, processed, config);
    note.push("prepend");
  }
  // transpose
  else if (tech === 'transpose') {
    rules = translateTranspose(tech, args, line, parsed, source, mapping, processed, config);
    note.push("transpose_related");
  } else if (tech === 'partial_transpose_column_to_row' ||
    tech === 'partial_transpose_row_to_column') {
    rules = translatePartialTranspose(tech, args, line, parsed, source, mapping, processed, config);
    note.push("transpose_related");
  }
  // facet column change
  else if (tech === 'change_n_facet_columns') {
    rules = translateChangeNFacetColumns(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === 'make_split') {
    rules = translateMakeSplit(tech, args, line, parsed, source, mapping, processed, config);
  }
  // [data]
  // data transform: aggregate, filter
  else if (tech === 'aggregate') {
    rules = translateAggregate(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === 'add_data_filter') {
    rules = translateAddDataFilter(tech, args, line, parsed, source, mapping, processed, config);
    note.push("prepend");
  }
  else if (tech === 'aggregate_levels') {
    rules = translateAggregateLevels(tech, args, line, parsed, source, mapping, processed, config);
    note.push("prepend");
  }
  else if (tech === 'disaggregate') {
    rules = translateDisaggregate(tech, args, line, parsed, source, mapping, processed, config);
  }
  // [mark & encoding]
  // making heatmap
  else if (tech === 'make_heatmap_no_agg_dup') {
    rules = translateMakeHeatmapNoAggDup(tech, args, line, parsed, source, mapping, processed, config);
  } else if (tech === 'make_heatmap_nontabular_no_agg_convert_channel') {
    rules = translateMakeHeatmapNonTabularNoAggConvertChannel(tech, args, line, parsed, source, mapping, processed, config);
  } else if (tech === 'make_heatmap_nontabular_with_remove_nominal_channels') {
    rules = translateMAkeHeatmapNonTabularRemoveNominalChannels(tech, args, line, parsed, source, mapping, processed, config);
  } else if (tech === 'reposition_mark_label_heatmap') {
    rules = translateRepositionMarkLabelForHeatmap(tech, args, line, parsed, source, mapping, processed, config);
  } else if (tech === 'make_heatmap_no_agg') {
    rules = translateMakeHeatmapNoAgg(tech, args, line, parsed, source, mapping, processed, config);
  } else if (tech === 'make_heatmap_nominal_color_to_fill') {
    rules = translateMakeheatmapNominalColorToFill(tech, args, line, parsed, source, mapping, processed, config)
  }
  // 1D dotplot to bar
  else if (tech === 'oneD_dotplot_to') {
    rules = translateDot2(tech, args, line, parsed, source, mapping, processed, config);
  }
  // compressing 2D dotplot
  else if (tech === 'compress_2d_dotplot') {
    rules = translateCompress2dDotPlot(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === 'bar_to_oneD_dotplot') {
    rules = translateBarTo1dDotPlot(tech, args, line, parsed, source, mapping, processed, config);
  }
  // map => others
  else if (tech === 'map_to_bars_n_pos_X_q_pos') {
    rules = translateMap2BarsNposXQpos(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === 'map_to_multiples') {
    rules = translateMap2Multiples(tech, args, line, parsed, source, mapping, processed, config);
  }
  // mark decoration
  else if (tech === 'remove_point_on_mark') {
    rules = translateRemovePointOnMark(tech, args, line, parsed, source, mapping, processed, config);
  } else if (tech === 'add_point_on_mark') {
    rules = translateAddPointOnMark(tech, args, line, parsed, source, mapping, processed, config);
  } else if (tech === 'convert_area_to_line') {
    rules = translateConvertAreaToLine(tech, args, line, parsed, source, mapping, processed, config);
  } else if (tech === 'convert_line_to_area') {
    rules = translateConvertLineToArea(tech, args, line, parsed, source, mapping, processed, config);
  } else if (tech === 'remove_end_mark') {
    rules = translateRemoveEndMark(tech, args, line, parsed, source, mapping, processed, config);
  } else if (tech === 'add_end_mark') {
    rules = translateAddEndMark(tech, args, line, parsed, source, mapping, processed, config);
  } else if (tech === 'invert_circle_mark') {
    rules = translateInvertCircleMark(tech, args, line, parsed, source, mapping, processed, config)
  }
  // encoding
  else if (tech === 'rescale_size_channel' || tech === 'rescale_size') {
    rules = translateRescaleSizeChannel(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === 'remove_channel') {
    rules = translateRemoveChannel(tech, args, line, parsed, source, mapping, processed, config);
  }
  // small
  else if (tech === 'remove_mark_label') {
    rules = translateRemoveMarkLabel(tech, args, line, parsed, source, mapping, processed, config);
    note.push("removals");
  }
  else if (tech === 'replace_mark_label_to_axis_label') {
    rules = translateReplaceMarkLabel2AxisLabel(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === 'add_mark_label') {
    rules = translateAddMarkLabel(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === 'remove_axis_grid') {
    rules = translateRemoveAxisGrid(tech, args, line, parsed, source, mapping, processed, config);
  }
  // text-related
  else if (tech === 'convert_axis_label_to_legend') {
    rules = translateConvertAxisLabelToLegend(tech, args, line, parsed, source, mapping, processed, config);
    note.push("prepend");
  }
  else if (tech === 'convert_legend_to_axis_label') {
    rules = translateConvertLegendToAxisLabel(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === 'convert_mark_label_to_axis_label') {
    rules = translateConvertMarkLabelToAxisLabel(tech, args, line, parsed, source, mapping, processed, config)
  }
  else if (tech === 'convert_mark_label_to_legend') {
    rules = translateConvertMarkLabelToLegend(tech, args, line, parsed, source, mapping, processed, config)
  }
  else if (tech === 'wrap_text') {
    rules = translateWrapText(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === 'wrap_text_by') {
    rules = translateWrapTextBy(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === 'unwrap_text') {
    rules = translateUnwrapText(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === 'unwrap_text_by') {
    rules = translateWrapTextBy(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === 'remove_text') {
    rules = translateRemoveText(tech, args, line, parsed, source, mapping, processed, config);
    note.push("removals");
  }
  else if (tech === 'remove_text_partial') {
    rules = translateRemoveTextPartial(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === 'add_axis_label') {
    rules = translateAddAxisLabel(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === 'add_text_partial') {
    rules = translateAddTextPartial(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === 'reposition_legend') {
    rules = translateRepositionLegend(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === 'add_legend') {
    rules = translateAddLegend(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === 'reduce_text_size') {
    rules = translateReduceTextSize(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === 'externalize_mark_label') {
    rules = translateExternalizeMarkLabel(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === 'number_mark_label') {
    rules = translateNumberMarkLabel(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === 'unwrap_br_text') {
    rules = translateUnwrapBrText(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === 'simplify_text_min_max_split') {
    rules = translateSimplifyTextMinMaxSplit(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === 'change_text_flex') {
    rules = translateChangeTextFlex(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === 'parallelize_text') {
    rules = translateParallelizeText(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === 'convert_text_orient') {
    rules = translateConvertTextOrient(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === 'remove_tick_values') {
    rules = translateRemoveTickValues(tech, args, line, parsed, source, mapping, processed, config);
  }
  // annotation-related
  else if (tech === 'wrap_annotation_by') {
    rules = translateWrapAnnotationBy(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === 'unwrap_annotation_by') {
    rules = translateWrapAnnotationBy(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === 'remove_annotation') {
    rules = translateRemoveAnnotation(tech, args, line, parsed, source, mapping, processed, config);
    note.push("removals");
  }
  else if (tech === "externalize_annotation") {
    rules = translateExternalizeAnnotation(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === "externalize_data_annotations") {
    rules = translateExternalizeDataAnnotation(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === "number_annotation") {
    rules = translateNumberAnnotation(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === "internalize_annotation") {
    rules = translateInternalizeAnnotation(tech, args, line, parsed, source, mapping, processed, config)
  }
  else if (tech === "convert_annotation_to_title") {
    rules = translateConvertAnnotation2Title(tech, args, line, parsed, source, mapping, processed, config);
  }
  // interaction-related
  else if (tech === "add_zoom_to_map") {
    rules = translateAddZoomToMap(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === "add_context_to_bar") {
    rules = translateAddContextToBar(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === "remove_tooltip") {
    rules = translateRemoveTooltip(tech, args, line, parsed, source, mapping, processed, config);
    note.push("removals");
  }
  else if (tech === "add_tooltip") {
    rules = translateAddTooltip(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === "fix_tooltip") {
    rules = translateRepositionTooltip(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === "unfix_tooltip") {
    rules = translateRepositionTooltip(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === "add_tooltip_field") {
    rules = translateAddTooltipField(tech, args, line, parsed, source, mapping, processed, config);
  }
  // title-related
  else if (tech === "remove_title") {
    rules = translateRemoveTitle(tech, args, line, parsed, source, mapping, processed, config);
    note.push("removals");
  }
  else if (tech === "internalize_title") {
    rules = translateInternalizeTitle(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === "reduce_title_font_size") {
    rules = translateReduceTitleFontSize(tech, args, line, parsed, source, mapping, processed, config);
  }
  else if (tech === "ignore_title_width") {
    rules = translateIgnoreTitleWidth(tech, args, line, parsed, source, mapping, processed, config);
  }
  // for watches
  else if (tech === "leave_min_max_on") {
    rules = translateLeaveMinMax(tech, args, line, parsed, source, mapping, processed, config);
    note.push("prepend");
  }
  else if (tech === "leave_highlights_only") {
    rules = translateLeaveHighlightOnly(tech, args, line, parsed, source, mapping, processed, config);
    note.push("prepend");
  }
  else if (tech === "line_multiples_to_overlays") {
    rules = translateLineMultiplesToOveralys(tech, args, line, parsed, source, mapping, processed, config);
    note.push("prepend");
  }
  else if (tech === "aggregate_multiples") {
    rules = translateAggregateMultiples(tech, args, line, parsed, source, mapping, processed, config);
    note.push("prepend");
  }
  else if (tech === "add_mark_highlights") {
    rules = translateAddMarkHighlights(tech, args, line, parsed, source, mapping, processed, config);
  }
  // position encoding adjustment
  if ((tech === 'disproportionate_resizing' || tech === 'proportionate_resizing') && config.has_old_new_size) {
    let sub_rules = addTransposeAdjustment(tech, args, line, parsed, source, mapping, processed, config);
    rules.push(...sub_rules);
  }

  return { rules, note };
}
